(function () {
  "use strict";

  const DEBOUNCE_MS = 280;
  const MIN_CHARS = 2;
  const MAX_RESULTS = 6;
  const SEARCH_FIELDS =
    "title,product_type,variants.title,vendor,tag";
  const i18n = window.hrcI18n || {};
  const t = (key, fallback) => i18n[key] || fallback;

  const modal = document.getElementById("hrc-search-modal");
  const input = document.getElementById("hrc-search-modal-input");
  const form = document.getElementById("hrc-search-modal-form");

  const stateDefault = modal?.querySelector("[data-search-default]");
  const stateLoading = modal?.querySelector("[data-search-loading]");
  const stateResults = modal?.querySelector("[data-search-results]");
  const stateEmpty = modal?.querySelector("[data-search-empty]");

  const resultsHeader = modal?.querySelector("[data-results-header]");
  const resultsList = modal?.querySelector("[data-results-list]");
  const resultsFooter = modal?.querySelector("[data-results-footer]");
  const emptyTerm = modal?.querySelector("[data-empty-term]");

  let debounceTimer = null;
  let abortController = null;
  let activeQuery = "";
  let requestSeq = 0;

  if (!modal) return;

  // Fixed header stacking context only blurs navbar; mount on body for full-page backdrop.
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  function openModal() {
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    setTimeout(() => input?.focus(), 50);
    showState("default");
  }

  function closeModal({ clearInput = true } = {}) {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    if (input && clearInput) input.value = "";
    activeQuery = "";
    requestSeq += 1;
    abortController?.abort();
  }

  document.querySelectorAll("[data-search-toggle]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault?.();
      modal.classList.contains("is-open") ? closeModal() : openModal();
    });
  });

  modal.querySelectorAll("[data-search-modal-close]").forEach((el) => {
    el.addEventListener("click", closeModal);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modal.classList.contains("is-open")) closeModal();
  });

  function showState(state) {
    const map = {
      default: stateDefault,
      loading: stateLoading,
      results: stateResults,
      empty: stateEmpty,
    };
    Object.values(map).forEach((el) => el?.classList.remove("is-active"));
    map[state]?.classList.add("is-active");
  }

  function scheduleSearch(q) {
    clearTimeout(debounceTimer);
    abortController?.abort();

    if (q.length < MIN_CHARS) {
      activeQuery = "";
      showState("default");
      return;
    }

    // Same query already rendered — keep results, don't abort/refetch.
    if (q === activeQuery && stateResults?.classList.contains("is-active")) {
      return;
    }

    showState("loading");
    debounceTimer = setTimeout(() => fetchResults(q), DEBOUNCE_MS);
  }

  input?.addEventListener("input", () => {
    scheduleSearch(input.value.trim());
  });

  // IME composition end can fire without a useful intermediate input on some browsers.
  input?.addEventListener("compositionend", () => {
    scheduleSearch(input.value.trim());
  });

  async function fetchSuggestProducts(q, signal) {
    const params = new URLSearchParams({
      q,
      "resources[type]": "product",
      "resources[limit]": String(MAX_RESULTS),
      "resources[options][unavailable_products]": "last",
      "resources[options][fields]": SEARCH_FIELDS,
    });
    const res = await fetch(`/search/suggest.json?${params.toString()}`, {
      signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("fetch error");
    const data = await res.json();
    return (data?.resources?.results?.products || []).filter(
      (product) => !window.HrcGift?.isGiftOnlyProduct?.(product),
    );
  }

  async function fetchCatalogFallback(q, signal) {
    const needle = q.toLocaleLowerCase("tr-TR");
    const matched = [];
    const res = await fetch(`/collections/all/products.json?limit=250&page=1`, {
      signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return matched;
    const data = await res.json();
    (data?.products || []).forEach((p) => {
      if (matched.length >= MAX_RESULTS) return;
      if (window.HrcGift?.isGiftOnlyProduct?.(p)) return;
      const hay = [p.title, p.vendor, p.product_type, p.tags]
        .flat()
        .join(" ")
        .toLocaleLowerCase("tr-TR");
      if (!hay.includes(needle)) return;
      const variant = (p.variants && p.variants[0]) || {};
      matched.push({
        id: p.id,
        title: p.title,
        handle: p.handle,
        tags: p.tags || [],
        url: `/products/${p.handle}`,
        vendor: p.vendor || "",
        available: (p.variants || []).some((v) => v.available),
        price: variant.price != null ? Number(variant.price) : Number(p.price) || 0,
        compare_at_price_max: variant.compare_at_price
          ? Number(variant.compare_at_price)
          : 0,
        featured_image: p.images?.[0]
          ? { url: p.images[0].src || p.images[0] }
          : null,
        image: p.images?.[0]?.src || "",
      });
    });
    return matched;
  }

  async function fetchResults(q) {
    const seq = ++requestSeq;
    abortController = new AbortController();
    const { signal } = abortController;

    try {
      let products = await fetchSuggestProducts(q, signal);
      if (seq !== requestSeq) return;

      if (!products.length) {
        products = await fetchCatalogFallback(q, signal);
        if (seq !== requestSeq) return;
      }

      activeQuery = q;
      renderResults(q, products);
    } catch (err) {
      if (err.name === "AbortError") return;
      if (seq !== requestSeq) return;
      if (emptyTerm) emptyTerm.textContent = t("noResultsFor", 'No results found for "__TERMS__"').replace("__TERMS__", q).replace("{{ terms }}", q);
      showState("empty");
    }
  }

  function productImageUrl(p) {
    const raw =
      p.featured_image?.url ||
      (typeof p.featured_image === "string" ? p.featured_image : "") ||
      p.image ||
      "";
    if (!raw || typeof raw !== "string") return "";
    return raw.replace(
      /(\.[a-z0-9]+)(\?|$)/i,
      (match, ext, tail) => {
        if (!/\.(jpe?g|png|webp|gif)$/i.test(ext)) return match;
        return `_200x200${ext}${tail}`;
      },
    );
  }

  function numericPrice(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
  }

  function renderResults(q, products) {
    products = window.HrcGift?.filterCatalog?.(products) || products || [];
    if (!resultsHeader || !resultsList || !resultsFooter) {
      showState("empty");
      return;
    }

    if (!products.length) {
      if (emptyTerm) emptyTerm.textContent = t("noResultsFor", 'No results found for "__TERMS__"').replace("__TERMS__", q).replace("{{ terms }}", q);
      showState("empty");
      return;
    }

    const searchUrl = `/search?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(q)}`;
    resultsHeader.innerHTML = `
      <span class="hrc-results-meta">${t("resultsFoundHtml", "<strong>__COUNT__</strong> results found").replace("__COUNT__", String(products.length)).replace("{{ count }}", String(products.length))}</span>
      <a href="${searchUrl}" class="hrc-results-view-all">
        ${t("viewAll", "View all")}
        <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M1 5h8M5 1l4 4-4 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>
    `;

    resultsList.innerHTML = products
      .map((p) => {
        const imgSrc = productImageUrl(p);
        const imgHtml = imgSrc
          ? `<img src="${escHtml(imgSrc)}" alt="${escHtml(p.title)}" class="hrc-search-result-item__img" width="64" height="64" loading="lazy">`
          : `<svg viewBox="0 0 64 64" fill="none" class="hrc-search-result-item__img" aria-hidden="true"><rect width="64" height="64" fill="var(--hrc-bg-subtle)"/></svg>`;

        const isAvailable = p.available !== false;

        const priceNum = numericPrice(p.price);
        const comingSoon = !(priceNum > 0);
        const compareNum = numericPrice(p.compare_at_price_max);
        let displayPrice = priceNum;
        let displayCompare = compareNum;
        let webLabel = "";

        if (
          !comingSoon &&
          Number.isFinite(priceNum) &&
          priceNum > 0 &&
          window.HrcWebDiscount?.resolveDisplayPricing
        ) {
          const resolved = window.HrcWebDiscount.resolveDisplayPricing(
            Math.round(priceNum * 100),
            Number.isFinite(compareNum) ? Math.round(compareNum * 100) : 0,
          );
          displayPrice = resolved.price / 100;
          displayCompare = resolved.compare / 100;
          webLabel = resolved.webDiscount || resolved.b2bDiscount ? resolved.label : "";
        }

        const onSale =
          !comingSoon &&
          Number.isFinite(displayCompare) &&
          Number.isFinite(displayPrice) &&
          displayPrice > 0 &&
          displayCompare > displayPrice;
        const comingSoonLabel = t("comingSoon", "Coming Soon");
        const price = comingSoon ? comingSoonLabel : formatMoney(displayPrice);
        const oldPriceHtml =
          !comingSoon && onSale
          ? `<span class="hrc-search-result-item__price-old">${formatMoney(displayCompare)}</span>`
          : "";
        const isB2B = window.HrcB2B?.isCustomer?.();
        const webBadgeHtml = !comingSoon && isB2B
          ? `<span class="hrc-search-result-item__web-discount" data-b2b-badge hidden></span>`
          : !comingSoon && webLabel
          ? `<span class="hrc-search-result-item__web-discount">${escHtml(webLabel)}</span>`
          : "";
        const stockClass = comingSoon
          ? "is-coming-soon"
          : isAvailable
            ? "is-instock"
            : "is-soldout";
        const stockLabel = comingSoon
          ? comingSoonLabel
          : isAvailable
            ? t("inStock", "In Stock")
            : t("soldOut", "Sold Out");
        const catalogMoney = comingSoon ? "" : formatMoney(priceNum);
        const priceInner = comingSoon
          ? `<span class="hrc-search-result-item__price is-coming-soon">${escHtml(price)}</span>`
          : isB2B && typeof window.HrcB2B?.priceMarkup === "function"
          ? window.HrcB2B.priceMarkup({
              productId: p.id || "",
              catalogText: catalogMoney,
              currentClass: "hrc-search-result-item__price",
              compareClass: "hrc-search-result-item__price-old",
            })
          : `<span class="hrc-search-result-item__price price-item price-item--regular money ${onSale ? "is-sale price-item--sale" : ""}">${escHtml(price)}</span>${oldPriceHtml}`;

        return `
        <a href="${escHtml(p.url || "#")}" class="hrc-search-result-item">
          <div class="hrc-search-result-item__img-wrap">${imgHtml}</div>
          <div class="hrc-search-result-item__info">
            <span class="hrc-search-result-item__vendor">${escHtml(p.vendor || "")}</span>
            <span class="hrc-search-result-item__title">${highlightQuery(escHtml(p.title), q)}</span>
            <span class="hrc-search-result-item__stock ${stockClass}">${stockLabel}</span>
            ${webBadgeHtml}
          </div>
          <div class="hrc-search-result-item__price-wrap price"${isB2B && !comingSoon ? " data-hrc-clay-price" : ""} data-product-id="${escHtml(String(p.id || ""))}" data-base-price="${escHtml(String(Math.round(priceNum * 100)))}">
            ${priceInner}
          </div>
        </a>
      `;
      })
      .join("");

    window.HrcB2B?.notifyPricingApps?.(resultsList);

    resultsFooter.innerHTML = `
      <a href="${searchUrl}" class="hrc-results-submit-btn">
        ${escHtml(t("viewAllFor", 'View all results for "__TERMS__"').replace("__TERMS__", q).replace("{{ terms }}", q))}
      </a>
    `;

    showState("results");
  }

  function escHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function highlightQuery(title, q) {
    try {
      const escaped = String(q || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      if (!escaped) return title;
      return title.replace(new RegExp(`(${escaped})`, "gi"), "<mark>$1</mark>");
    } catch {
      return title;
    }
  }

  function formatMoney(amount) {
    if (window.HrcFormatMoney) {
      return window.HrcFormatMoney(amount, { fromMajor: true, forceDecimals: true });
    }
    const n = Number(amount);
    if (!Number.isFinite(n)) return "";
    const abs = Math.round(Math.abs(n) * 100);
    const grouped = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${grouped},${String(abs % 100).padStart(2, "0")}TL`;
  }

  form?.addEventListener("submit", () => {
    closeModal({ clearInput: false });
  });
})();
